import { supportsMultipleReferences } from "@app/hooks/firefly";
import { getVariationReferenceKeys } from "@app/views/projects/wizardSteps";

import {
  CAMPAIGN_OVERRIDES,
  interpolatePrompt,
  resolveValues,
} from "./variationPrompts";
import { getVariationType } from "./variationTypes";

/**
 * Turning a variation into a request.
 *
 * Both halves of "what does this variation ask for" are resolved here, from
 * the same `VARIATION_TYPES` entry: the prompt template that gets it a
 * `visualPrompt`, and the payload that generates it.
 */

/**
 * Template-variation fields that are not model options.
 *
 * `prompt` and `values` are consumed when building the `visualPrompt` — letting
 * them through would spread the raw, un-interpolated `{{Var}}` body over the
 * generated prompt. `referenceKey` is resolved into `uploadId`/`uploadIds`,
 * `skipReferenceSeed` only controls seed inheritance, and the rest is
 * authoring metadata.
 */
const NON_MODEL_FIELDS = new Set([
  "name",
  "label",
  "type",
  "useCases",
  "description",
  "imageDescriptionPrompt",
  "prompt",
  "values",
  "referenceKey",
  "skipReferenceSeed",
]);

const modelOptionsOf = (templateVariation) =>
  Object.fromEntries(
    Object.entries(templateVariation ?? {}).filter(
      ([field]) => !NON_MODEL_FIELDS.has(field),
    ),
  );

/**
 * The values `{{Var}}` placeholders are expanded from for a variation:
 *
 *   campaign → CAMPAIGN_OVERRIDES[templateId][key] → variation.values
 *
 * Also returns the variation's references, primary first — builders that only
 * know about a single reference keep reading `visual`.
 */
const resolvePromptScope = (
  variation,
  { templateId, campaign, visual, visuals } = {},
) => {
  const references = visuals?.length ? visuals : [visual].filter(Boolean);
  const primary = references[0] ?? visual;

  const buildOverrides = CAMPAIGN_OVERRIDES[templateId]?.[variation?.key];
  const merged = {
    ...campaign,
    ...buildOverrides?.({ campaign, visual: primary, visuals: references }),
  };
  const scope = { ...merged, ...resolveValues(variation?.values, merged) };

  return { scope, references, primary };
};

/**
 * Expands the `{{Var}}` placeholders of a prompt written by the user the way a
 * static prompt is expanded — from the same scope, dropping the lines that
 * reference a value the campaign does not have. Returns `null` when nothing is
 * left. Takes the arguments of `getVariationPromptTemplate`.
 */
export const compileUserPrompt = (prompt, variation, context) =>
  interpolatePrompt(prompt, resolvePromptScope(variation, context).scope);

/**
 * The single place an image variation's prompt template is created.
 *
 * Resolution order, most general to most specific:
 *
 *   campaign → CAMPAIGN_OVERRIDES[templateId][key] → variation.values
 *
 * The resulting `scope` is used twice, which is what lets `variation.values`
 * work for *every* type: it's handed to the builder as `campaign` (so the
 * hardcoded builders that inline `${campaign?.["X"]}` honour an override with
 * no change of their own), and it expands `{{Var}}` in whatever they returned.
 *
 * Returns a Zoltar ID/user-message request, a plain prompt string, or `null`
 * when the variation type has no prompt configured.
 */
export const getVariationPromptTemplate = (
  variation,
  { templateId, campaign, customer, visual, visuals } = {},
) => {
  const buildPrompt = getVariationType(variation?.type)?.prompt;

  if (!buildPrompt) {
    return null;
  }

  const { scope, references, primary } = resolvePromptScope(variation, {
    templateId,
    campaign,
    visual,
    visuals,
  });

  return interpolatePrompt(
    buildPrompt({
      prompt: variation?.prompt,
      campaign: scope,
      customer,
      visual: primary,
      visuals: references,
    }),
    scope,
  );
};

/**
 * A variation's prompt when it is static — a template filled with campaign
 * values, so it can be recomputed at any time at no cost — or `null` when it
 * is written by an LLM (or not configured). Takes the arguments of
 * `getVariationPromptTemplate`.
 */
export const resolveStaticPrompt = (variation, context) => {
  const template = getVariationPromptTemplate(variation, context);

  return typeof template === "string" ? template : null;
};

/**
 * Whether a variation's prompt can be written without an LLM — a static
 * prompt (see `resolveStaticPrompt`), or none at all, as for a Photoshop
 * action — so a changed campaign never needs a prompt request for it.
 */
export const isStaticPrompt = (variation, context) => {
  const template = getVariationPromptTemplate(variation, context);

  return typeof template === "string" || template == null;
};

/**
 * Resolve a variation's references to their Firefly upload ids, in template
 * order — the `uploadIds` that `buildVariationPayload` consumes.
 *
 * Returns `null` when an *expected* reference has not finished uploading yet:
 * its asset can exist (so a filePath-based gate passes) while its Firefly upload
 * is still in flight, or its presign wasn't ready so it never entered the upload
 * queue. Generating in that window would drop it — a single-reference variation
 * would generate with no reference at all, and a multi-reference one (e.g.
 * `Combine`) would silently shift the rest, turning the background into
 * "Reference 1". The caller should wait and retry once more uploads resolve.
 *
 * Otherwise returns the ordered, defined upload ids (possibly empty when the
 * variation references nothing, or only unexpected placeholder keys).
 *
 * @param {object} variation - The template variation.
 * @param {object} context
 * @param {Record<string, string | null | undefined>} [context.byKey] -
 *   reference key → its Firefly upload id (or nullish while pending).
 * @param {(key: string) => boolean} [context.isReferenceExpected] - whether a
 *   reference key points at an image that exists or is still to be generated.
 * @returns {string[] | null}
 */
export const resolveVariationUploadIds = (
  variation,
  { byKey = {}, isReferenceExpected = () => false } = {},
) => {
  const referenceKeys = getVariationReferenceKeys(variation);
  const orderedUploadIds = referenceKeys.map((key) => byKey[key]);

  const isWaitingOnUpload = referenceKeys.some(
    (key, index) => isReferenceExpected(key) && !orderedUploadIds[index],
  );

  if (isWaitingOnUpload) {
    return null;
  }

  return orderedUploadIds.filter(Boolean);
};

/**
 * The request that generates a variation, or `null` when it cannot be built —
 * an unconfigured type, or an action whose source image is missing.
 *
 * Everything type-dependent comes from the type's entry; everything
 * variation-dependent comes from the arguments:
 *
 * @param {object} variation - The variation as persisted on the video: its
 *   `key`, `type`, generated `visualPrompt` and `videoSettings`.
 * @param {object} context
 * @param {object} [context.templateVariation] - The template's definition of
 *   the same variation; everything on it that is a model option is spread into
 *   the payload.
 * @param {Array<object>} [context.references] - Its reference images, resolved,
 *   primary first.
 * @param {Array<string>} [context.uploadIds] - The Firefly upload ids of those
 *   references, in the same order, missing ones dropped.
 */
export const buildVariationPayload = (
  variation,
  { templateVariation, references = [], uploadIds = [] } = {},
) => {
  const definition = getVariationType(variation?.type);

  if (!definition) {
    console.warn("Unknown variation type:", variation);
    return null;
  }

  const { model, defaultModel, reference = "upload" } = definition;

  const modelOptions = modelOptionsOf(templateVariation);
  const primary = references[0];

  // Inherit the reference image's seed when it has one — a variation derived
  // from an already-generated asset stays visually consistent with it. `0` is
  // the "model returned no seed" sentinel from the Firefly response
  // normalizer, so it doesn't count. An explicit seed on the template
  // variation still wins. A template variation with `skipReferenceSeed: true`
  // opts out of the inheritance.
  const referenceSeed =
    !templateVariation?.skipReferenceSeed &&
    typeof primary?.seed === "number" &&
    primary.seed > 0
      ? primary.seed
      : null;

  const payload = {
    ...(referenceSeed === null ? {} : { seed: referenceSeed }),
    ...modelOptions,
    // A type either pins its model or only suggests one, leaving the template
    // variation free to pick another.
    model: model ?? modelOptions.model ?? defaultModel,
  };

  if (!payload.model) {
    console.warn("Variation type has no model configured:", variation);
    return null;
  }

  if (definition.action) {
    payload.action = definition.action;
  } else {
    payload.prompt = variation?.visualPrompt;
  }

  if (definition.negativePrompt) {
    payload.negativePrompt = definition.negativePrompt;
  }

  if (definition.videoSettings && variation?.videoSettings) {
    payload.videoSettings = variation.videoSettings;
  }

  switch (reference) {
    // Operates on the reference image itself, so it needs a source file rather
    // than a prompt or a Firefly upload id.
    case "file":
      if (!primary?.filePath) {
        console.warn("Missing reference image for variation:", variation);
        return null;
      }

      payload.filePath = primary.filePath;
      break;

    // Generated from the prompt alone.
    case "none":
      break;

    default:
      payload.uploadId = uploadIds[0];

      // Only the Google models accept more than one reference image — every
      // other model keeps generating from the primary one.
      if (!!payload.uploadId && uploadIds.length > 1) {
        if (supportsMultipleReferences(payload.model)) {
          payload.uploadIds = uploadIds;
        } else {
          console.warn(
            `Model "${payload.model}" supports a single reference image — generating "${variation?.key}" from "${getVariationReferenceKeys(templateVariation)[0]}" only.`,
          );
        }
      }
      break;
  }

  // The generated asset records the single image it is filed under.
  payload.referenceKey = getVariationReferenceKeys(templateVariation)[0];

  return payload;
};
